import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import PlatformConnectionScreen, { type PlatformConnectionStatus } from './components/PlatformConnectionScreen'
import {
  isQuickBooksResultMessage,
  PLATFORM_OAUTH_CALLBACK,
  type QuickBooksCallbackMessage,
} from './connectionMessages'
import useAutoCloseOnSuccess from './useAutoCloseOnSuccess'

/**
 * OAuth callback target for QuickBooks (Intuit redirects to `${origin}/quickbooks`).
 * This page runs inside the auth popup: it relays the authorization `code` and
 * `realmId` back to the opener window (the Platform page / EditPlatformModal) and
 * closes itself. The actual token exchange happens in the opener, which is
 * authenticated, so the user context is attached to the exchange request.
 */
export default function QuickBooksCallbackPage() {
  const [searchParams] = useSearchParams()
  const callbackSentRef = useRef(false)
  const [status, setStatus] = useState<PlatformConnectionStatus>('connecting')
  const [errorMessage, setErrorMessage] = useState<string>()

  useEffect(() => {
    if (callbackSentRef.current) return
    callbackSentRef.current = true

    const oauthError = searchParams.get('error')
    const oauthErrorDescription = searchParams.get('error_description')
    if (oauthError) {
      setStatus('error')
      setErrorMessage(oauthErrorDescription || 'QuickBooks authorization was not completed.')
      return
    }

    const code = searchParams.get('code')
    const realmId = searchParams.get('realmId')
    if (!code || !realmId) {
      setStatus('error')
      setErrorMessage('The QuickBooks callback is missing required authorization details.')
      return
    }
    if (!window.opener) {
      setStatus('error')
      setErrorMessage('Dashboard Insights could not be found. Return to the application and try again.')
      return
    }

    const callback: QuickBooksCallbackMessage = {
      type: PLATFORM_OAUTH_CALLBACK,
      platform: 'QuickBooks',
      code,
      realmId,
    }
    window.opener.postMessage(callback, window.location.origin)
  }, [searchParams])

  useEffect(() => {
    const listener = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== window.opener) return
      if (!isQuickBooksResultMessage(event.data)) return

      if (event.data.status === 'success') {
        setStatus('success')
        return
      }
      setStatus('error')
      setErrorMessage(event.data.error)
    }

    window.addEventListener('message', listener)
    return () => window.removeEventListener('message', listener)
  }, [])

  useAutoCloseOnSuccess(status)

  return <PlatformConnectionScreen platformName="QuickBooks" status={status} errorMessage={errorMessage} />
}
